/*
  lab-search.js
  Working search for the LAB theme. Dependency free, loaded with defer.
  Included by sections/lab-header.liquid and sections/lab-main-search.liquid;
  the window guard keeps the second include inert.

  Two data modes behind one render shape:
  1. Local: /assets/lab-search-index.json exists (the preview build emits
     it). Case-insensitive substring match on title and type, title-start
     matches ranked first, prices formatted client side from subunits.
  2. Fallback: the index 404s (real Shopify). Predictive search API at
     /search/suggest.json, adapted to the same shape, prices used as the
     formatted strings the API returns. The /search page is then rendered
     server side by lab-main-search.liquid and this script leaves it alone.
*/
(function () {
  'use strict';

  if (window.__labSearchLoaded) return;
  window.__labSearchLoaded = true;

  var DEBOUNCE_MS = 150;
  var OVERLAY_LIMIT = 8;
  var INDEX_URL = '/assets/lab-search-index.json';
  var SUGGEST_URL = '/search/suggest.json';

  var indexMode = 'unknown'; /* becomes 'local' or 'fallback' */
  var indexItems = null;
  var indexPromise = null;

  var lastFocused = null;
  var pageActive = false;

  /* ------------------------------------------------------------------ */
  /* Data                                                                */
  /* ------------------------------------------------------------------ */

  function loadIndex() {
    if (!indexPromise) {
      indexPromise = fetch(INDEX_URL)
        .then(function (response) {
          if (!response.ok) throw new Error('index missing');
          return response.json();
        })
        .then(function (data) {
          indexMode = 'local';
          indexItems = Array.isArray(data) ? data : [];
        })
        .catch(function () {
          indexMode = 'fallback';
          indexItems = null;
        });
    }
    return indexPromise;
  }

  /* Index prices are subunit integers; match the Liquid money filter. */
  function formatMoney(subunits) {
    var pounds = subunits / 100;
    var wholePence = subunits % 100 === 0;
    return '£' + pounds.toLocaleString('en-GB', {
      minimumFractionDigits: wholePence ? 0 : 2,
      maximumFractionDigits: wholePence ? 0 : 2
    });
  }

  function localResults(query, limit) {
    var needle = query.toLowerCase();
    var starts = [];
    var contains = [];

    (indexItems || []).forEach(function (item) {
      var title = String(item.title || '').toLowerCase();
      var type = String(item.type || '').toLowerCase();
      if (title.indexOf(needle) === 0) {
        starts.push(item);
      } else if (title.indexOf(needle) !== -1 || type.indexOf(needle) !== -1) {
        contains.push(item);
      }
    });

    var matched = starts.concat(contains);
    if (limit > 0) matched = matched.slice(0, limit);

    return matched.map(function (item) {
      return {
        url: item.url || '',
        title: item.title || '',
        image: item.image || '',
        priceLabel: formatMoney(parseInt(item.price, 10) || 0)
      };
    });
  }

  function fallbackResults(query, limit) {
    var url = SUGGEST_URL +
      '?q=' + encodeURIComponent(query) +
      '&resources[type]=product&limit=' + (limit > 0 ? limit : OVERLAY_LIMIT);

    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('suggest failed');
        return response.json();
      })
      .then(function (data) {
        var products =
          (data &&
            data.resources &&
            data.resources.results &&
            data.resources.results.products) || [];

        return products.map(function (product) {
          var image = product.featured_image && product.featured_image.url;
          var price = String(product.price || '');
          if (price && price.indexOf('£') === -1) price = '£' + price;
          return {
            url: product.url || '',
            title: product.title || '',
            image: image || '',
            priceLabel: price
          };
        });
      })
      .catch(function () {
        return [];
      });
  }

  function search(query, limit) {
    return loadIndex().then(function () {
      if (indexMode === 'local') return localResults(query, limit);
      return fallbackResults(query, limit);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Render helpers                                                      */
  /* ------------------------------------------------------------------ */

  function element(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  function searchUrl(query) {
    return '/search?q=' + encodeURIComponent(query);
  }

  /* The index stores 200w thumbs; ask for more pixels in grid cells and
     leave unrecognised URL shapes untouched. */
  function gridImage(url) {
    if (!url) return url;
    if (url.indexOf('width=200') !== -1) return url.replace('width=200', 'width=800');
    if (url.indexOf('_200x') !== -1) return url.replace('_200x', '_800x');
    return url;
  }

  function buildRow(item) {
    var row = element('a', 'lab-search-result');
    row.href = item.url;

    var thumb = element('span', 'lab-search-result__thumb');
    if (item.image) {
      var img = document.createElement('img');
      img.src = item.image;
      img.alt = '';
      img.width = 48;
      img.height = 48;
      img.loading = 'lazy';
      thumb.appendChild(img);
    }
    row.appendChild(thumb);

    row.appendChild(element('span', 'lab-search-result__title font-display', item.title));
    row.appendChild(element('span', 'lab-search-result__price tracked-caps', item.priceLabel));
    return row;
  }

  function renderOverlay(container, items, query) {
    container.innerHTML = '';

    if (!items.length) {
      container.appendChild(
        element('p', 'lab-search-panel__empty', 'No pieces found for ' + query)
      );
      return;
    }

    items.forEach(function (item) {
      container.appendChild(buildRow(item));
    });

    var all = element('div', 'lab-search-panel__all');
    var link = element('a', 'tracked-caps lab-link', 'View all results');
    link.href = searchUrl(query);
    all.appendChild(link);
    container.appendChild(all);
  }

  /* ------------------------------------------------------------------ */
  /* Header overlay panel                                                */
  /* ------------------------------------------------------------------ */

  function getPanel() {
    return document.getElementById('LabSearchPanel');
  }

  function getScrim() {
    return document.querySelector('[data-lab-search-scrim]');
  }

  function setToggles(expanded) {
    var toggles = document.querySelectorAll('[data-lab-search-toggle]');
    for (var i = 0; i < toggles.length; i += 1) {
      toggles[i].setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
  }

  function openPanel() {
    var panel = getPanel();
    if (!panel) return;
    var scrim = getScrim();

    lastFocused = document.activeElement;
    panel.hidden = false;
    panel.classList.add('is-open');
    if (scrim) scrim.hidden = false;
    setToggles(true);
    loadIndex();

    var input = panel.querySelector('[data-lab-search-input]');
    if (input) {
      input.focus();
      input.select();
    }
  }

  function closePanel(restoreFocus) {
    var panel = getPanel();
    var scrim = getScrim();

    if (scrim) scrim.hidden = true;
    if (panel && !panel.hidden) {
      panel.hidden = true;
      panel.classList.remove('is-open');
    }
    setToggles(false);

    if (restoreFocus && lastFocused && document.contains(lastFocused)) {
      lastFocused.focus();
    }
    lastFocused = null;
  }

  /* lab-base.js dismisses the panel on Escape (data-lab-dismissible);
     sync the scrim and hand focus back to the toggle. */
  document.addEventListener('lab:dismiss', function (event) {
    var panel = getPanel();
    if (panel && event.target === panel) {
      closePanel(true);
    }
  });

  document.addEventListener('click', function (event) {
    if (!(event.target instanceof Element)) return;

    var toggle = event.target.closest('[data-lab-search-toggle]');
    if (toggle) {
      event.preventDefault();
      var panel = getPanel();
      if (panel && panel.hidden) {
        openPanel();
      } else {
        closePanel(true);
      }
      return;
    }

    if (event.target.closest('[data-lab-search-scrim]')) {
      closePanel(true);
    }
  });

  var overlayTimer = null;
  var overlayToken = 0;

  function runOverlaySearch(value) {
    var panel = getPanel();
    var container = panel && panel.querySelector('[data-lab-search-results]');
    if (!container) return;

    var query = value.trim();
    var token = (overlayToken += 1);

    if (!query) {
      container.innerHTML = '';
      return;
    }

    search(query, OVERLAY_LIMIT).then(function (items) {
      if (token !== overlayToken) return;
      renderOverlay(container, items, query);
    });
  }

  /* ------------------------------------------------------------------ */
  /* The /search page (client rendered in local mode only)               */
  /* ------------------------------------------------------------------ */

  function piecesLabel(count) {
    return count === 1 ? '1 piece' : count + ' pieces';
  }

  function buildCard(item) {
    var card = element('a', 'lab-card');
    card.href = item.url;

    var media = element('figure', 'lab-card__media');
    if (item.image) {
      var img = document.createElement('img');
      img.src = gridImage(item.image);
      img.alt = item.title;
      img.loading = 'lazy';
      img.className = 'lab-card__img lab-card__img--primary';
      media.appendChild(img);
    }
    card.appendChild(media);

    var meta = element('div', 'lab-card__meta');
    meta.appendChild(element('p', 'lab-card__title font-display', item.title));
    meta.appendChild(element('p', 'lab-card__price tracked-caps', item.priceLabel));
    card.appendChild(meta);
    return card;
  }

  function renderPage(query) {
    var root = document.querySelector('[data-lab-search-page]');
    if (!root) return;

    var container = root.querySelector('[data-lab-search-page-results]');
    var count = root.querySelector('[data-lab-search-count]');
    if (!container) return;

    container.innerHTML = '';

    if (!query) {
      if (count) count.textContent = '';
      return;
    }

    var items = localResults(query, 0);

    if (!items.length) {
      if (count) count.textContent = '';
      container.appendChild(
        element('p', 'lab-main-search__empty', 'No pieces found for ' + query)
      );
      return;
    }

    if (count) count.textContent = piecesLabel(items.length);

    var grid = element('div', 'lab-main-search__grid');
    items.forEach(function (item) {
      grid.appendChild(buildCard(item));
    });
    container.appendChild(grid);
  }

  function updatePageQuery(query) {
    var url = window.location.pathname +
      (query ? '?q=' + encodeURIComponent(query) : '');
    window.history.replaceState(null, '', url);
    renderPage(query);
  }

  var pageTimer = null;

  document.addEventListener('input', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    if (target.hasAttribute('data-lab-search-input')) {
      clearTimeout(overlayTimer);
      overlayTimer = setTimeout(function () {
        runOverlaySearch(target.value);
      }, DEBOUNCE_MS);
      return;
    }

    if (pageActive && target.hasAttribute('data-lab-search-page-input')) {
      clearTimeout(pageTimer);
      pageTimer = setTimeout(function () {
        updatePageQuery(target.value.trim());
      }, DEBOUNCE_MS);
    }
  });

  function initPage() {
    var root = document.querySelector('[data-lab-search-page]');
    if (!root) return;

    var params = new URLSearchParams(window.location.search);
    var query = (params.get('q') || '').trim();

    loadIndex().then(function () {
      /* Real Shopify renders the page server side; leave it alone. */
      if (indexMode !== 'local') return;

      pageActive = true;
      var input = root.querySelector('[data-lab-search-page-input]');
      if (input && !input.value) input.value = query;
      renderPage(query);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPage);
  } else {
    initPage();
  }
})();
